import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import CustomChip from "../../../components/CustomChip";
import CustomImageTable from "../../../components/CustomImageTable";
import CustomTooltip from "../../../components/CustomTooltip";
import DropDownMenuItem from "../../../components/DropDown/DropDownMenuItem";
import DropDownMenuProfile from "../../../components/DropDown/DropDownMenuProfile";
import {
    CancelIconsOptions,
    DetailsIconsOptions,
    EmailSubsectionChip,
    IconAvatarTable,
    IconClockTable,
    IconMoreVerticalDots,
    IconScheduleTable,
    LinkIconsOptions
} from "../../../components/buttons/IconButtons";
import TableFunction from "../../../components/table/TableFunction";
import CustomTd from "../../../components/table/CustomTd";
import CustomTh from "../../../components/table/CustomTh";
import { DEFAULT_LEFT, SEARCH_MODEL } from "../../../components/table/Model/TableModel";
import { TABLES } from "../../../Apis/Tables";
import { EMPTY_VALUES, getDateFormat, getTimeFormat } from "../../../Apis/functions";
import { colors } from "../../../enums/colors";
import { SimpleSearchWithId } from "../../../reduxToolkit/Search/SearchApi";
import { CancelInvitation } from "../../../reduxToolkit/Invitation/InvitationApi";
import { INVITATION_VIEW_TYPE, invitationInTime } from "../../Dashboard/Visitor/Invitation/Model/Model";
import { MY_APARTMENT_VISIT_MODEL } from "../Model/Model";
import { getVisitorTypeLabel } from "../../Dashboard/Visitor/Type/Model/Model";
import {statusBorderColor, statusColor, statusFontColor} from "../../../enums/statusColorEnum";
import {status, STATUS_MODEL} from "../../../enums/statusEnum";

/**
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 *
 * Invitations logic of my apartment: paginated search of the visits created on
 * the apartment of the logged resident, table columns, search and the details,
 * invitation link, invite again and cancel actions of every row.
 *
 * The list is searched through the USER_INVITATION model of the simple search by
 * id, which takes the zone of the apartment as the modelId of the body, the
 * resident as the userId and the INVITATION_ZONE_ID view type.
 *
 * @param {Object} props
 * @param {string} props.zoneId - Zone of the apartment of the logged resident, the invitations are created on it.
 * @param {boolean} props.modified - Flag raised when an invitation is created or cancelled so the list is reloaded.
 * @param {Function} props.setModified - Lowers the flag once the list is loaded again.
 * @returns {Object} Localization function, table state and the row handlers.
 */
export const MyApartmentInvitationsTableApp = ({ zoneId, modified, setModified }) => {
    const dispatch = useDispatch();
    // Logged resident, they have no employee record behind getUserByIdAtEntry so
    // the user of the session is the one the search runs with
    const userId = sessionStorage.getItem('id');
    const tableId = "tbl_mbit_invitations";
    const searchId = "txt_mbit_search_simple";
    const option = TABLES.USER_INVITATION;
    const [searchElement, setSearchElement] = useState("");
    const [results, setResults] = useState({ content: [], totalElements: 0 });
    const [detailsModal, setDetailsModal] = useState(false);
    const [invitationLinkModal, setInvitationLinkModal] = useState(false);
    const [cancelModal, setCancelModal] = useState(false);
    const [inviteAgainModal, setInviteAgainModal] = useState(false);
    const [itemSelected, setItemSelected] = useState(null);
    // Flags SearchSimple works with, the search itself runs through getData
    const [search, setSearch] = useState(false);
    const [simpleSearch, setSimpleSearch] = useState(false);
    // Zone the last fetch ran with, the detail is not loaded yet on mount
    const requestedZoneId = useRef(null);
    // Scrollable container of the table, panelTables-container sizes it with --top-value
    const elementRef = useRef(null);

    /**
     * Loads the invitations page the table renders.
     *
     * @param {number} page - Page requested by the pagination, zero based.
     * @param {Object} [options] - Extra values sent by TableFunction.
     * @param {number} [options.rowsPerPage] - Page size in use.
     * @param {string} [options.searchElement] - Search value the fetch runs with.
     */
    const getData = (page, options) => {
        // The zone is required by the search, on the first render of the detail
        // it is still loading and the fetch is left to the effect below
        if (!zoneId) return;

        requestedZoneId.current = zoneId;

        const body = SEARCH_MODEL.setObject({
            page,
            size: options?.rowsPerPage || rowsPerPage,
            model: option,
            key: options?.searchElement ?? searchElement,
            modelId: zoneId,
            userId,
            viewType: INVITATION_VIEW_TYPE.INVITATION_ZONE_ID
        });

        dispatch(SimpleSearchWithId({ body })).then(
            ({ payload: { data: { data, success } } }) => {
                if (success !== true) return;

                setResults({
                    ...data,
                    content: MY_APARTMENT_VISIT_MODEL.setList(data?.content)
                });
            }
        );
    };

    const getThead = () => {
        return [
            <CustomTh tableId={tableId} fixedXY={true} left={DEFAULT_LEFT} classes="first_head">{t('visitor_name')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('visitor_type')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('start_date_at_time')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('end_date_at_time')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('identity_validation_status')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('status')}</CustomTh>
        ];
    };

    /**
     * Date and time of a visit, the same stacked layout the invitation tables use.
     *
     * @param {string} date - Date coming from the visit.
     * @returns {JSX.Element}
     */
    const dateAndTime = (date) => (
        <>
            <div className={"d-flex align-items-center"}>
                <div>
                    <IconScheduleTable className={"mr-8px mb-1 icon-color-general-950"}/>
                </div>
                {getDateFormat(date, false)}
            </div>
            <div className={"d-flex align-items-center"}>
                <div>
                    <IconClockTable className={"mr-8px mb-1 icon-color-general-950"}/>
                </div>
                {getTimeFormat(date)}
            </div>
        </>
    );

    const getTbody = () => {
        if (!results || results?.content?.length === 0) return {
            body: [],
            data: []
        };

        const body = results?.content?.map((visit) => {
            // Answer of the visitor to the invitation, it is the chip painted
            // under their name
            const visitorStatusId = visit?.visitorStatusId;
            // Status of the invitation itself, the one the cancel of the row writes
            const statusId = visit?.statusId;
            const identityValidationId = visit?.identityValidation ?? null;

            return (
            <>
                <CustomTd tableId={tableId} fixed={true} left={DEFAULT_LEFT} classes={'first-column'}>
                    <CustomImageTable image={IconAvatarTable} containerClass={'d-flex align-items-center'}>
                        <div className={'ml-1'}>
                            <CustomTooltip
                                text={visit?.visitorFullName}
                                classes={'fs-14px font-color-general-950 ibl-font-w-600 text-ellipsis d-block'}
                                showIfTruncated={true}
                            />
                            <CustomChip
                                background={statusColor[visitorStatusId]}
                                fontColor={statusFontColor[visitorStatusId]}
                                borderColor={statusBorderColor[visitorStatusId]}
                                legend={t(status[visitorStatusId]) || EMPTY_VALUES.DEFAULT}
                                variant={"large"}
                            />
                        </div>
                    </CustomImageTable>
                </CustomTd>
                <CustomTd tableId={tableId}>
                    <CustomChip
                        background={colors.secondary100}
                        fontColor={colors.secondary700}
                        borderColor={colors.secondary150}
                        legend={getVisitorTypeLabel(t, visit?.visitorType)}
                        variant={'compact'}
                    />
                </CustomTd>
                <CustomTd tableId={tableId}>
                    {dateAndTime(visit?.startDate)}
                </CustomTd>
                <CustomTd tableId={tableId}>
                    {dateAndTime(visit?.endDate)}
                </CustomTd>
                <CustomTd tableId={tableId}>
                    <CustomChip
                        background={statusColor[identityValidationId]}
                        fontColor={statusFontColor[identityValidationId]}
                        borderColor={statusBorderColor[identityValidationId]}
                        legend={t(status[identityValidationId]) || EMPTY_VALUES.DEFAULT}
                        variant={'large'}
                    />
                </CustomTd>
                <CustomTd tableId={tableId}>
                    <CustomChip
                        background={statusColor[statusId]}
                        fontColor={statusFontColor[statusId]}
                        borderColor={statusBorderColor[statusId]}
                        legend={t(status[statusId]) || EMPTY_VALUES.DEFAULT}
                        variant={'large'}
                    />
                </CustomTd>
            </>
            );
        });

        return {
            body,
            data: results?.content
        };
    };

    // Use TableFunction to handle the table logic (pagination, actions menu)
    const {
        t,
        thead,
        tbody,
        rowsPerPage,
        openMenu,
        handleMenuToggle,
        handleClose,
        pagination
    } = TableFunction({
        getTbody,
        getThead,
        data: results,
        labelRowsPerPage: 'invitation_per_page',
        hasContainer: true,
        fixedPagination: false,
        searchElement,
        getData,
        modified,
        setModified
    });

    // Loads the list once the zone of the detail arrives, the mount fetch of
    // TableFunction is skipped while there is no zone to search with
    useEffect(() => {
        if (!zoneId || requestedZoneId.current === zoneId) return;

        setModified(true);
    }, [zoneId]);

    /**
     * Distance from the top of the viewport to the table, panelTables-container
     * takes the rest of the screen with it and leaves the pagination outside
     * of the scroll.
     */
    useLayoutEffect(() => {
        if (!elementRef?.current) return;

        const distanceTop = elementRef.current.getBoundingClientRect().top + 90;
        elementRef.current.style.setProperty('--top-value', `${distanceTop}px`);
    }, [results?.content]);

    /**
     * Search of SearchSimple, the invitations need the apartment on the body so
     * the fetch is the same getData of the table instead of the generic one.
     */
    const handleSearch = () => getData(0);

    /**
     * Reset of SearchSimple, brings the first page back without the search key.
     */
    const handleResetSearch = () => getData(0, { searchElement: "" });

    /**
     * Closes the details modal and clears the visit it was showing.
     */
    const hideDetails = () => {
        setDetailsModal(false);
        setItemSelected(null);
    };

    /**
     * Closes the cancel confirmation and clears the visit it was showing.
     */
    const hideCancel = () => {
        setCancelModal(false);
        setItemSelected(null);
    };

    /**
     * Cancels the selected visit, the same endpoint the ongoing invitations of
     * the dashboard cancel with. The confirmation modal closes the flow itself,
     * the list is reloaded once the invitation comes back cancelled.
     */
    const handleCancel = () => {
        if (!itemSelected?.id) return;

        dispatch(CancelInvitation({ ids: [itemSelected?.id] })).then(
            ({ payload: { data: { success } } }) => {
                if (success !== true) return;

                toast.success(t('has_been_cancelled_successfully'));
                setModified(true);
            }
        );
    };

    /**
     * Closes the invite again modal and clears the visit it was showing.
     */
    const hideInviteAgain = () => {
        setInviteAgainModal(false);
        setItemSelected(null);
    };

    /**
     * Vertical three dots menu with the actions available for one visit.
     *
     * @param {Object} item - Visit data.
     * @param {number} index - Row position, used for the menu item ids.
     * @returns {JSX.Element}
     */
    const getActionsButtons = (item, index) => {
        // A cancelled or overdue invitation is only read again or sent as a new
        // one, its link no longer takes the visitor anywhere
        const isCanceled = item?.statusId === STATUS_MODEL.CANCELED
            || item?.visitorStatusId === STATUS_MODEL.CANCELED;
        const isOverdue = !invitationInTime(item?.endDate);
        // The invitation can only be cancelled while it is alive: not cancelled
        // already, not denied, not rejected by the visitor and still in time
        const canBeCancelled = !isCanceled
            && !isOverdue
            && item?.approvalStatusId !== STATUS_MODEL.DENIED
            && item?.visitorStatusId !== STATUS_MODEL.REJECTED;

        return (
            <DropDownMenuProfile
                openMenu={openMenu}
                handleMenuToggle={handleMenuToggle}
                menuId={`menu-${item?.id}`}
                icon={<IconMoreVerticalDots />}
            >
                <DropDownMenuItem
                    id={`btn_mbit_details_${item?.id}`}
                    index={index}
                    handleClose={handleClose}
                    name={'details'}
                    icon={<DetailsIconsOptions/>}
                    click={() => {
                        setItemSelected(item);
                        setDetailsModal(true);
                    }}
                />
                {!isCanceled && !isOverdue &&
                    <DropDownMenuItem
                        id={`btn_mbit_invitation_link_${item?.id}`}
                        index={index}
                        handleClose={handleClose}
                        name={'invitation_link'}
                        icon={<LinkIconsOptions/>}
                        click={() => {
                            setItemSelected(item);
                            setInvitationLinkModal(true);
                        }}
                    />
                }
                <DropDownMenuItem
                    id={`btn_mbit_invite_again_${item?.id}`}
                    index={index}
                    handleClose={handleClose}
                    name={'invite_again'}
                    icon={<EmailSubsectionChip width={22} height={22}/>}
                    click={() => {
                        setItemSelected(item);
                        setInviteAgainModal(true);
                    }}
                />
                {canBeCancelled &&
                    <DropDownMenuItem
                        id={`btn_mbit_cancel_${item?.id}`}
                        index={index}
                        handleClose={handleClose}
                        name={'cancel'}
                        fontColor={'font-color-error-600'}
                        iconColor={'icon-color-error-code-600'}
                        icon={<CancelIconsOptions/>}
                        click={() => {
                            setItemSelected(item);
                            setCancelModal(true);
                        }}
                    />
                }
            </DropDownMenuProfile>
        );
    };

    return {
        t,
        tableId,
        searchId,
        option,
        searchElement,
        setSearchElement,
        results,
        setResults,
        rowsPerPage,
        search,
        setSearch,
        simpleSearch,
        setSimpleSearch,
        handleSearch,
        handleResetSearch,
        thead,
        tbody,
        detailsModal,
        invitationLinkModal,
        setInvitationLinkModal,
        cancelModal,
        inviteAgainModal,
        itemSelected,
        setItemSelected,
        hideDetails,
        hideCancel,
        hideInviteAgain,
        handleCancel,
        getActionsButtons,
        pagination,
        elementRef,
    };
};
